 
/**
 * Hook for syncing order filters with URL query parameters
 *
 * This hook synchronizes Jotai atoms with URL query parameters, enabling:
 * - Page reload persistence
 * - Shareable filter URLs
 * - Browser back/forward navigation
 *
 * Uses the same parameter structure as the API for consistency
 */
import { useAtomValue,useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { OrdersServiceSort } from '@ekhodealer/ekho-common';

import type { SortDirection } from '@ekhodealer/ekho-common/rev';

import { CommonDateFilter as OrderDateFilter } from '@common/types/filters';
import { parseArray, parseBoolean, parseNumber } from '@common/utils/parse';

import {
	actionRequiredFilterAtom,
	currentPageAtom,
	debouncedSearchValueAtom,
	financingStatusFilterAtom,
	fulfillmentDateCustomRangeAtom,
	fulfillmentDateFilterAtom,
	fulfillmentTypeFilterAtom,
	itemsPerPageAtom,
	modelFilterAtom,
	orderDateCustomRangeAtom,
	orderDateFilterAtom,
	paymentChoiceFilterAtom,
	paymentStatusFilterAtom,
	registrationStateFilterAtom,
	sortConfigAtom,
	typeFilterAtom,
} from '../atoms/ordersAtoms';
import { DeliveryDateFilter } from '../types/filters';

/**
 * Map sort parameter to column and direction
 * Converts OrdersServiceSort enum values to UI sort state
 */
const mapSortParamToConfig = (
	sortParam: string,
): { column: string; direction: SortDirection } | null => {
	const sortMap: Record<string, { column: string; direction: SortDirection }> = {
		[OrdersServiceSort.ORDER_DATE_ASC]: { column: 'order-id', direction: 'ascending' },
		[OrdersServiceSort.ORDER_DATE_DESC]: { column: 'order-id', direction: 'descending' },
		[OrdersServiceSort.BUYER_ASC]: { column: 'buyer', direction: 'ascending' },
		[OrdersServiceSort.BUYER_DESC]: { column: 'buyer', direction: 'descending' },
		[OrdersServiceSort.ORDERS_TABLE_TAB_ASC]: { column: 'order-stage', direction: 'ascending' },
		[OrdersServiceSort.ORDERS_TABLE_TAB_DESC]: {
			column: 'order-stage',
			direction: 'descending',
		},
		[OrdersServiceSort.VEHICLE_ASC]: { column: 'vehicle', direction: 'ascending' },
		[OrdersServiceSort.VEHICLE_DESC]: { column: 'vehicle', direction: 'descending' },
		[OrdersServiceSort.PGA_ASC]: { column: 'pga', direction: 'ascending' },
		[OrdersServiceSort.PGA_DESC]: { column: 'pga', direction: 'descending' },
		[OrdersServiceSort.FI_ASC]: { column: 'fi', direction: 'ascending' },
		[OrdersServiceSort.FI_DESC]: { column: 'fi', direction: 'descending' },
		[OrdersServiceSort.FI_PRU_ASC]: { column: 'fi-profit', direction: 'ascending' },
		[OrdersServiceSort.FI_PRU_DESC]: { column: 'fi-profit', direction: 'descending' },
		[OrdersServiceSort.TOTAL_ASC]: { column: 'total', direction: 'ascending' },
		[OrdersServiceSort.TOTAL_DESC]: { column: 'total', direction: 'descending' },
		[OrdersServiceSort.PAYMENT_CHOICE_ASC]: {
			column: 'payment-choice',
			direction: 'ascending',
		},
		[OrdersServiceSort.PAYMENT_CHOICE_DESC]: {
			column: 'payment-choice',
			direction: 'descending',
		},
		[OrdersServiceSort.PAYMENT_STATUS_ASC]: { column: 'payment', direction: 'ascending' },
		[OrdersServiceSort.PAYMENT_STATUS_DESC]: { column: 'payment', direction: 'descending' },
		[OrdersServiceSort.FINANCING_STATUS_ASC]: { column: 'financing', direction: 'ascending' },
		[OrdersServiceSort.FINANCING_STATUS_DESC]: { column: 'financing', direction: 'descending' },
		[OrdersServiceSort.REG_STATE_ASC]: { column: 'registration-state', direction: 'ascending' },
		[OrdersServiceSort.REG_STATE_DESC]: {
			column: 'registration-state',
			direction: 'descending',
		},
		[OrdersServiceSort.VIN_ASC]: { column: 'vin', direction: 'ascending' },
		[OrdersServiceSort.VIN_DESC]: { column: 'vin', direction: 'descending' },
		[OrdersServiceSort.FULFILLMENT_TYPE_ASC]: {
			column: 'fulfillment-type',
			direction: 'ascending',
		},
		[OrdersServiceSort.FULFILLMENT_TYPE_DESC]: {
			column: 'fulfillment-type',
			direction: 'descending',
		},
		[OrdersServiceSort.FULFILLMENT_DATE_ASC]: {
			column: 'est-fulfillment-date',
			direction: 'ascending',
		},
		[OrdersServiceSort.FULFILLMENT_DATE_DESC]: {
			column: 'est-fulfillment-date',
			direction: 'descending',
		},
		[OrdersServiceSort.TASKS_ASC]: { column: 'tasks', direction: 'ascending' },
		[OrdersServiceSort.TASKS_DESC]: { column: 'tasks', direction: 'descending' },
	};

	return sortMap[sortParam] || null;
};

/**
 * Map column and direction to sort parameter
 * Converts UI sort state to OrdersServiceSort enum value
 */
const mapConfigToSortParam = (column: string, direction: SortDirection): string | null => {
	if (direction === 'none') return null;

	const sortMap: Record<string, { asc: OrdersServiceSort; desc: OrdersServiceSort }> = {
		'order-id': {
			asc: OrdersServiceSort.ORDER_DATE_ASC,
			desc: OrdersServiceSort.ORDER_DATE_DESC,
		},
		buyer: {
			asc: OrdersServiceSort.BUYER_ASC,
			desc: OrdersServiceSort.BUYER_DESC,
		},
		'order-stage': {
			asc: OrdersServiceSort.ORDERS_TABLE_TAB_ASC,
			desc: OrdersServiceSort.ORDERS_TABLE_TAB_DESC,
		},
		'order-date': {
			asc: OrdersServiceSort.ORDER_DATE_ASC,
			desc: OrdersServiceSort.ORDER_DATE_DESC,
		},
		vehicle: {
			asc: OrdersServiceSort.VEHICLE_ASC,
			desc: OrdersServiceSort.VEHICLE_DESC,
		},
		pga: {
			asc: OrdersServiceSort.PGA_ASC,
			desc: OrdersServiceSort.PGA_DESC,
		},
		fi: {
			asc: OrdersServiceSort.FI_ASC,
			desc: OrdersServiceSort.FI_DESC,
		},
		'fi-profit': {
			asc: OrdersServiceSort.FI_PRU_ASC,
			desc: OrdersServiceSort.FI_PRU_DESC,
		},
		total: {
			asc: OrdersServiceSort.TOTAL_ASC,
			desc: OrdersServiceSort.TOTAL_DESC,
		},
		'payment-choice': {
			asc: OrdersServiceSort.PAYMENT_CHOICE_ASC,
			desc: OrdersServiceSort.PAYMENT_CHOICE_DESC,
		},
		payment: {
			asc: OrdersServiceSort.PAYMENT_STATUS_ASC,
			desc: OrdersServiceSort.PAYMENT_STATUS_DESC,
		},
		financing: {
			asc: OrdersServiceSort.FINANCING_STATUS_ASC,
			desc: OrdersServiceSort.FINANCING_STATUS_DESC,
		},
		'registration-state': {
			asc: OrdersServiceSort.REG_STATE_ASC,
			desc: OrdersServiceSort.REG_STATE_DESC,
		},
		vin: {
			asc: OrdersServiceSort.VIN_ASC,
			desc: OrdersServiceSort.VIN_DESC,
		},
		'fulfillment-type': {
			asc: OrdersServiceSort.FULFILLMENT_TYPE_ASC,
			desc: OrdersServiceSort.FULFILLMENT_TYPE_DESC,
		},
		'est-fulfillment-date': {
			asc: OrdersServiceSort.FULFILLMENT_DATE_ASC,
			desc: OrdersServiceSort.FULFILLMENT_DATE_DESC,
		},
		tasks: {
			asc: OrdersServiceSort.TASKS_ASC,
			desc: OrdersServiceSort.TASKS_DESC,
		},
	};

	const columnSort = sortMap[column];
	if (!columnSort) return null;

	return direction === 'ascending' ? columnSort.asc : columnSort.desc;
};

/**
 * Hook to synchronize order filters with URL query parameters
 */
export const useOrdersFiltersUrl = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const isInitialized = useRef(false);
	const isSyncingFromUrl = useRef(false);

	// Setters for all filter atoms
	const setSearchValue = useSetAtom(debouncedSearchValueAtom);
	const setActionRequired = useSetAtom(actionRequiredFilterAtom);
	const setPaymentChoice = useSetAtom(paymentChoiceFilterAtom);
	const setPaymentStatus = useSetAtom(paymentStatusFilterAtom);
	const setFinancingStatus = useSetAtom(financingStatusFilterAtom);
	const setModels = useSetAtom(modelFilterAtom);
	const setTypes = useSetAtom(typeFilterAtom);
	const setRegistrationState = useSetAtom(registrationStateFilterAtom);
	const setOrderDate = useSetAtom(orderDateFilterAtom);
	const setOrderDateCustomRange = useSetAtom(orderDateCustomRangeAtom);
	const setFulfillmentType = useSetAtom(fulfillmentTypeFilterAtom);
	const setFulfillmentDate = useSetAtom(fulfillmentDateFilterAtom);
	const setFulfillmentDateCustomRange = useSetAtom(fulfillmentDateCustomRangeAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);
	const setItemsPerPage = useSetAtom(itemsPerPageAtom);
	const setSortConfig = useSetAtom(sortConfigAtom);

	// Getters for all filter atoms (to sync to URL)
	const searchValue = useAtomValue(debouncedSearchValueAtom);
	const actionRequired = useAtomValue(actionRequiredFilterAtom);
	const paymentChoice = useAtomValue(paymentChoiceFilterAtom);
	const paymentStatus = useAtomValue(paymentStatusFilterAtom);
	const financingStatus = useAtomValue(financingStatusFilterAtom);
	const models = useAtomValue(modelFilterAtom);
	const types = useAtomValue(typeFilterAtom);
	const registrationState = useAtomValue(registrationStateFilterAtom);
	const orderDate = useAtomValue(orderDateFilterAtom);
	const orderDateCustomRange = useAtomValue(orderDateCustomRangeAtom);
	const fulfillmentType = useAtomValue(fulfillmentTypeFilterAtom);
	const fulfillmentDate = useAtomValue(fulfillmentDateFilterAtom);
	const fulfillmentDateCustomRange = useAtomValue(fulfillmentDateCustomRangeAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const sortConfig = useAtomValue(sortConfigAtom);

	// Initialize atoms from URL on mount
	useEffect(() => {
		if (isInitialized.current) return;

		isSyncingFromUrl.current = true;

		// Parse URL params and set atoms
		const urlQuery = searchParams.get('query');
		if (urlQuery) {
			setSearchValue(urlQuery);
		}

		// Note: Tab is managed through route path (/:servicesProviderId/orders/:tabId)
		// not query parameters, so we don't sync it here

		const urlActionRequired = searchParams.get('actionRequired');
		if (urlActionRequired !== null) {
			setActionRequired(parseBoolean(urlActionRequired));
		}

		const urlPaymentChoice = searchParams.get('paymentChoice');
		if (urlPaymentChoice) {
			setPaymentChoice(parseArray(urlPaymentChoice));
		}

		const urlPaymentStatus = searchParams.get('paymentStatus');
		if (urlPaymentStatus) {
			setPaymentStatus(parseArray(urlPaymentStatus));
		}

		const urlFinancingStatus = searchParams.get('financingStatus');
		if (urlFinancingStatus) {
			setFinancingStatus(parseArray(urlFinancingStatus));
		}

		const urlModels = searchParams.get('models');
		if (urlModels) {
			setModels(parseArray(urlModels));
		}

		const urlTypes = searchParams.get('types');
		if (urlTypes) {
			setTypes(parseArray(urlTypes));
		}

		const urlRegistrationState = searchParams.get('registrationState');
		if (urlRegistrationState) {
			setRegistrationState(urlRegistrationState);
		}

		const urlOrderDate = searchParams.get('orderDate');
		if (
			urlOrderDate &&
			Object.values(OrderDateFilter).includes(urlOrderDate as OrderDateFilter)
		) {
			setOrderDate(urlOrderDate as OrderDateFilter);
		}

		// Parse custom order date range
		const urlOrderDateFrom = searchParams.get('orderDateFrom');
		const urlOrderDateTo = searchParams.get('orderDateTo');
		if (urlOrderDateFrom || urlOrderDateTo) {
			setOrderDateCustomRange({
				from: urlOrderDateFrom ? parseNumber(urlOrderDateFrom, 0) || null : null,
				to: urlOrderDateTo ? parseNumber(urlOrderDateTo, 0) || null : null,
			});
		}

		const urlFulfillmentType = searchParams.get('fulfillmentType');
		if (urlFulfillmentType === 'delivery' || urlFulfillmentType === 'pickup') {
			setFulfillmentType(urlFulfillmentType);
		}

		const urlFulfillmentDate = searchParams.get('fulfillmentDate');
		if (
			urlFulfillmentDate &&
			Object.values(DeliveryDateFilter).includes(urlFulfillmentDate as DeliveryDateFilter)
		) {
			setFulfillmentDate(urlFulfillmentDate as DeliveryDateFilter);
		}

		// Parse custom fulfillment date range
		const urlFulfillmentDateFrom = searchParams.get('fulfillmentDateFrom');
		const urlFulfillmentDateTo = searchParams.get('fulfillmentDateTo');
		if (urlFulfillmentDateFrom || urlFulfillmentDateTo) {
			setFulfillmentDateCustomRange({
				from: urlFulfillmentDateFrom
					? parseNumber(urlFulfillmentDateFrom, 0) || null
					: null,
				to: urlFulfillmentDateTo ? parseNumber(urlFulfillmentDateTo, 0) || null : null,
			});
		}

		const urlPage = searchParams.get('page');
		if (urlPage) {
			setCurrentPage(parseNumber(urlPage, 1));
		}

		const urlItemsPerPage = searchParams.get('itemsPerPage');
		if (urlItemsPerPage) {
			setItemsPerPage(parseNumber(urlItemsPerPage, 10));
		}

		const urlSort = searchParams.get('sortBy');
		if (urlSort) {
			const sortConfigFromUrl = mapSortParamToConfig(urlSort);
			if (sortConfigFromUrl) {
				setSortConfig([sortConfigFromUrl]);
			}
		}

		isInitialized.current = true;
		isSyncingFromUrl.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount, setters are stable
	}, []); // Only run on mount - setters are stable and don't need to be in deps

	// Sync atoms to URL whenever they change
	useEffect(() => {
		// Skip if we're currently syncing from URL to avoid circular updates
		if (!isInitialized.current || isSyncingFromUrl.current) return;

		const params = new URLSearchParams();

		// Add query parameter
		if (searchValue) params.set('query', searchValue);

		// Note: Tab is managed through route path (/:servicesProviderId/orders/:tabId)
		// not query parameters, so we don't sync it here

		// Add action required filter
		if (actionRequired) params.set('actionRequired', 'true');

		// Add payment choice filters
		if (paymentChoice.length > 0) {
			params.set('paymentChoice', paymentChoice.join(','));
		}

		// Add payment status filters
		if (paymentStatus.length > 0) {
			params.set('paymentStatus', paymentStatus.join(','));
		}

		// Add financing status filters
		if (financingStatus.length > 0) {
			params.set('financingStatus', financingStatus.join(','));
		}

		// Add model filters
		if (models.length > 0) {
			params.set('models', models.join(','));
		}

		// Add type filters
		if (types.length > 0) {
			params.set('types', types.join(','));
		}

		// Add registration state filter
		if (registrationState) {
			params.set('registrationState', registrationState);
		}

		// Add order date filter
		if (orderDate) {
			params.set('orderDate', orderDate);
		}

		// Add custom order date range
		if (orderDate === OrderDateFilter.CUSTOM) {
			if (orderDateCustomRange.from !== null) {
				params.set('orderDateFrom', orderDateCustomRange.from.toString());
			}
			if (orderDateCustomRange.to !== null) {
				params.set('orderDateTo', orderDateCustomRange.to.toString());
			}
		}

		// Add fulfillment type filter
		if (fulfillmentType) {
			params.set('fulfillmentType', fulfillmentType);
		}

		// Add fulfillment date filter
		if (fulfillmentDate) {
			params.set('fulfillmentDate', fulfillmentDate);
		}

		// Add custom fulfillment date range
		if (fulfillmentDate === DeliveryDateFilter.CUSTOM) {
			if (fulfillmentDateCustomRange.from !== null) {
				params.set('fulfillmentDateFrom', fulfillmentDateCustomRange.from.toString());
			}
			if (fulfillmentDateCustomRange.to !== null) {
				params.set('fulfillmentDateTo', fulfillmentDateCustomRange.to.toString());
			}
		}

		// Add page parameter (only if not 1)
		if (currentPage !== 1) {
			params.set('page', currentPage.toString());
		}

		// Add items per page parameter (only if not default 10)
		if (itemsPerPage !== 10) {
			params.set('itemsPerPage', itemsPerPage.toString());
		}

		// Add sort parameter
		if (sortConfig.length > 0) {
			const sort = sortConfig[0];
			if (sort) {
				const sortParam = mapConfigToSortParam(sort.column, sort.direction);
				if (sortParam) {
					params.set('sortBy', sortParam);
				}
			}
		}

		// Update URL without adding to history (use replace to avoid cluttering browser history)
		setSearchParams(params, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync URL when filter atoms change, setSearchParams is stable
	}, [
		searchValue,
		actionRequired,
		paymentChoice,
		paymentStatus,
		financingStatus,
		models,
		types,
		registrationState,
		orderDate,
		orderDateCustomRange,
		fulfillmentType,
		fulfillmentDate,
		fulfillmentDateCustomRange,
		currentPage,
		itemsPerPage,
		sortConfig,
	]);
};
